import React from "react";

import {
  CircleHelp,
  Clapperboard,
  FolderHeart,
  GalleryHorizontal,
  Image,
  LayoutGrid,
  Pencil,
  Puzzle,
  Redo2,
  ShoppingCart,
  Sofa,
  Star,
  Trash2,
  Tv,
} from "lucide-react";

import { SmartlistType } from "@/src/api/MediaAPI.types";
import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";

import DataTableColumnHeader from "../../../components/dataTable/DataTableColumnHeader";
import {
  actionsColumn,
  dragHandleColumn,
  statusColumn,
} from "../../../components/dataTable/dataTableColumns";
import DataTableLinkCell from "../../../components/dataTable/DataTableLinkCell";
import DataTableStatusDot from "../../../components/dataTable/DataTableStatusDot";
import { CELL_ICON_CLASS } from "../../../components/dataTable/dataTableStyles";
import {
  PROJECTS_ROUTE,
  smartlistEditPath,
} from "../../../components/routes/routes";
import { COMPONENT_TYPES } from "../../../constants/componentType";
import { getElementByUuid } from "../../../utils/data/Content";
import ItemStatus from "../../../utils/ItemStatus";

import type { TFunction } from "i18next";
import type { LucideIcon } from "lucide-react";

import type {
  ComponentStatus,
  CreatableComponentType,
  ModuleSource,
  PageComponentRow,
  SmartlistDataState,
} from "./pageComponentColumns.types";
import type {
  Platform,
  PlatformComponent,
  SmartlistModuleName,
} from "../../../api/ProjectAPI.types";
import type { SmartlistListItem } from "../../../api/SmartlistAPI.types";
import type {
  DataTableColumnDef,
  StatusOption,
  StatusTone,
} from "../../../components/dataTable/dataTable.types";

const { MOSAIC, CAROUSEL, WIDGET, HIGHLIGHT, FAQ, SHORTCUT } = COMPONENT_TYPES;

export const CREATABLE_COMPONENT_TYPE_LIST = [
  MOSAIC,
  CAROUSEL,
  WIDGET,
  HIGHLIGHT,
  FAQ,
  SHORTCUT,
] as const;

export const COMPONENT_TYPE_ICONS = {
  [MOSAIC]: LayoutGrid,
  [CAROUSEL]: GalleryHorizontal,
  [WIDGET]: Puzzle,
  [HIGHLIGHT]: Image,
  [FAQ]: CircleHelp,
  [SHORTCUT]: FolderHeart,
} as const satisfies Record<CreatableComponentType, LucideIcon>;

export const COMPONENT_TYPE_LABEL_KEYS = {
  [MOSAIC]: "mosaic",
  [CAROUSEL]: "carousel",
  [WIDGET]: "editor-widget:widget",
  [HIGHLIGHT]: "editor-highlight:highlight",
  [FAQ]: "faq",
  [SHORTCUT]: "shortcut",
} as const satisfies Record<CreatableComponentType, string>;

const MODULE_SOURCES: Partial<Record<SmartlistModuleName, ModuleSource>> = {
  my_list: { icon: Star, labelKey: "myList" },
  resume: { icon: Redo2, labelKey: "resume" },
  user_recommendation: { icon: Clapperboard, labelKey: "user_recommendation" },
  now: { icon: Tv, labelKey: "now" },
  purchase: { icon: ShoppingCart, labelKey: "purchase" },
};

const SMARTLIST_TONES: Record<string, StatusTone> = {
  active: "success",
  pause: "warning",
  stop: "error",
};

// Utilitaires en JavaScript, sans types : on déclare ici ce qu'ils renvoient.
const findSmartlist = getElementByUuid as (
  uuid: string,
  smartlists: SmartlistDataState,
) => SmartlistListItem | null;
const getStatus = ItemStatus.getStatus as (
  component: PlatformComponent,
) => ComponentStatus;
const getStatusWithSmartlist = ItemStatus.getComponentStatus as (
  component: PlatformComponent,
  smartlist: SmartlistListItem | undefined,
) => ComponentStatus;
const getSmartlistStatus = ItemStatus.getSmartlistStatus as (
  smartlist: SmartlistListItem,
) => string;

function getModuleSource({
  smartlist,
  component,
}: PageComponentRow): ModuleSource | undefined {
  if (smartlist?.type === SmartlistType.SERIES) {
    return { icon: Sofa, labelKey: "componentList.series" };
  }
  const moduleName = component.static_component?.smartlist_module_name;
  return !smartlist && moduleName ? MODULE_SOURCES[moduleName] : undefined;
}

function isCreatableType(
  type: PlatformComponent["type"],
): type is CreatableComponentType {
  return Object.keys(COMPONENT_TYPE_ICONS).includes(type);
}

export function getComponentHref(
  projectSlug: string,
  pageId: string,
  platform: Platform,
  type: string,
  uuid: string,
): string {
  return `${PROJECTS_ROUTE}/${projectSlug}/layout/pages/${pageId}/${platform}/${type}/${uuid}`;
}

function getSmartlist(
  component: PlatformComponent,
  smartlists: SmartlistDataState | undefined,
): SmartlistListItem | undefined {
  const smartlistUuid = component.dynamic_component?.smartlist_uuid;
  if (!smartlistUuid || !smartlists?.indexes?.uuid) return undefined;

  return findSmartlist(smartlistUuid, smartlists) ?? undefined;
}

function getComponentStatus(
  component: PlatformComponent,
  smartlist: SmartlistListItem | undefined,
): ComponentStatus {
  if (
    component.type === WIDGET ||
    component.type === HIGHLIGHT ||
    component.type === FAQ ||
    component.type === SHORTCUT
  ) {
    return getStatus(component);
  }

  return getStatusWithSmartlist(component, smartlist);
}

export function toPageComponentRow(
  component: PlatformComponent,
  smartlists?: SmartlistDataState,
): PageComponentRow {
  const smartlist = getSmartlist(component, smartlists);

  return {
    uuid: component.uuid,
    label: component.label,
    type: component.type,
    status: getComponentStatus(component, smartlist),
    smartlist,
    component,
  };
}

// Modifie les objets en place : ceux du cache portent l'ordre non enregistré.
export function applyComponentOrder(
  components: PlatformComponent[],
): PlatformComponent[] {
  components.forEach((component, index) => {
    const updated = component;
    updated.last_rank ??= updated.rank;
    updated.rank = index + 1;
  });

  return components;
}

function getFormat(component: PlatformComponent, t: TFunction): string {
  if (
    !("params" in component) ||
    !component.params ||
    !("ratio" in component.params) ||
    !component.params.ratio
  ) {
    return t("169");
  }

  return t(component.params.ratio);
}

function getStatusOptions(t: TFunction): StatusOption[] {
  return [
    { value: "active", label: t("active"), tone: "success" },
    { value: "pause", label: t("pause"), tone: "warning" },
    { value: "stop", label: t("stop"), tone: "error" },
  ];
}

interface PageComponentColumnsOptions {
  t: TFunction;
  projectSlug: string;
  pageId: string;
  platform: Platform;
  onEdit: (component: PageComponentRow) => void;
  onDelete: (componentId: string) => void;
}

export function pageComponentColumns({
  t,
  projectSlug,
  pageId,
  platform,
  onEdit,
  onDelete,
}: PageComponentColumnsOptions): DataTableColumnDef<PageComponentRow>[] {
  const status = statusColumn<PageComponentRow>(
    "status",
    t("componentList.status"),
    getStatusOptions(t),
  );

  const getName = (row: PageComponentRow): string => row.label ?? t("unamed");

  return [
    dragHandleColumn<PageComponentRow>(getName),
    { ...status, meta: { ...status.meta, filterOptions: undefined } },
    {
      id: "label",
      accessorKey: "label",
      enableSorting: false,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("label")} />
      ),
      meta: { label: t("label") },
      cell: ({ row }) => (
        <DataTableLinkCell
          href={getComponentHref(
            projectSlug,
            pageId,
            platform,
            row.original.type,
            row.original.uuid,
          )}
        >
          {getName(row.original)}
        </DataTableLinkCell>
      ),
    },
    {
      id: "smartlist",
      accessorFn: (row) => row.smartlist?.label,
      enableSorting: false,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("smartlist")} />
      ),
      meta: { label: t("smartlist") },
      cell: ({ row }) => {
        const { smartlist } = row.original;
        if (!smartlist) return null;

        return (
          <div className="flex items-center gap-2">
            <DataTableStatusDot
              tone={SMARTLIST_TONES[getSmartlistStatus(smartlist)] ?? "neutral"}
            />
            <DataTableLinkCell
              href={smartlistEditPath(projectSlug, "smartlist", smartlist.uuid)}
            >
              {smartlist.label}
            </DataTableLinkCell>
          </div>
        );
      },
    },
    {
      id: "format",
      accessorFn: (row) => getFormat(row.component, t),
      enableSorting: false,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("componentList.format")}
        />
      ),
      meta: {
        label: t("componentList.format"),
        width: "6%",
        minWidth: 70,
        align: "center",
      },
    },
    {
      id: "type",
      accessorFn: (row) => row.type,
      enableSorting: false,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("componentList.type")}
        />
      ),
      meta: {
        label: t("componentList.type"),
        width: "5%",
        minWidth: 50,
        align: "center",
      },
      cell: ({ row }) => {
        const { type } = row.original;
        if (!isCreatableType(type)) return null;

        const TypeIcon = COMPONENT_TYPE_ICONS[type];
        const typeLabel = t(COMPONENT_TYPE_LABEL_KEYS[type]);
        return (
          <span
            className="inline-flex align-middle"
            data-tooltip-content={typeLabel}
            data-tooltip-id={APP_TOOLTIP_ID}
          >
            <TypeIcon aria-hidden="true" className={CELL_ICON_CLASS} />
            <span className="sr-only">{typeLabel}</span>
          </span>
        );
      },
    },
    {
      id: "source",
      accessorFn: (row) =>
        row.component.static_component?.smartlist_module_name,
      enableSorting: false,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("componentList.source")}
        />
      ),
      meta: {
        label: t("componentList.source"),
        width: "6%",
        minWidth: 70,
        align: "center",
      },
      cell: ({ row }) => {
        const source = getModuleSource(row.original);
        if (!source) return null;

        const SourceIcon = source.icon;
        const sourceLabel = t(source.labelKey);
        return (
          <span
            className="inline-flex align-middle"
            data-tooltip-content={sourceLabel}
            data-tooltip-id={APP_TOOLTIP_ID}
          >
            <SourceIcon aria-hidden="true" className={CELL_ICON_CLASS} />
            <span className="sr-only">{sourceLabel}</span>
          </span>
        );
      },
    },
    actionsColumn<PageComponentRow>(
      (row) => [
        {
          label: t("componentList.edit"),
          icon: <Pencil />,
          onClick: () => onEdit(row),
        },
        {
          label: t("componentList.delete"),
          icon: <Trash2 />,
          variant: "destructive",
          onClick: () => onDelete(row.uuid),
          confirm: {
            title: t("deleteTitle"),
            description: t("deleteBody", { component: getName(row) }),
            confirmLabel: t("deleteConfirm"),
          },
        },
      ],
      getName,
    ),
  ];
}
